import { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Menu, Phone, Mail, MapPin, Facebook, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { CONTACT, SOCIALS, SITE_NAME } from "@/lib/site";
import { LOCAL_BUSINESS_JSONLD, WEBSITE_JSONLD } from "@/components/Seo";

const NAV_ITEMS = [
  { to: "/classes", label: "Classes" },
  { to: "/timetable", label: "Timetable" },
  { to: "/private-lessons", label: "Private Lessons" },
  { to: "/costume-hire", label: "Costume Hire" },
  { to: "/locations", label: "Locations" },
  { to: "/about", label: "About" },
  { to: "/blog", label: "Blog" },
];

const ALL_NAV = [...NAV_ITEMS, { to: "/past-students", label: "Past Students" }];

const Logo = () => (
  <Link to="/" data-testid="nav-logo-link" className="flex items-baseline gap-3 group shrink-0">
    <span className="font-display text-3xl sm:text-4xl leading-none text-foreground">
      KERRY<span className="text-[hsl(var(--kerrys-coral))]">&rsquo;S</span>
    </span>
    <span className="hidden xl:block text-[10px] font-bold uppercase tracking-[0.3em] text-muted-foreground">
      Whitstable
    </span>
  </Link>
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 bg-background border-b-[0.5px] border-foreground/30 print:hidden" data-testid="main-navbar">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-4 xl:gap-5" aria-label="Main navigation">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[11px] font-bold uppercase tracking-[0.14em] whitespace-nowrap transition-colors ${
                  isActive
                    ? "text-foreground underline underline-offset-8 decoration-[hsl(var(--kerrys-coral))] decoration-2"
                    : "text-muted-foreground hover:text-foreground"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3 shrink-0">
          <Button
            asChild
            data-testid="nav-book-button"
            className="hidden sm:inline-flex rounded-none bg-[hsl(var(--kerrys-coral))] text-foreground hover:bg-[hsl(18,95%,52%)] border-[0.5px] border-foreground font-bold uppercase tracking-[0.12em] text-xs px-5 h-10"
          >
            <Link to="/book">Book a Class</Link>
          </Button>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="lg:hidden rounded-none border-[0.5px] border-foreground"
                data-testid="nav-mobile-menu-button"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 bg-background border-l-[0.5px] border-foreground overflow-y-auto">
              <div className="mt-10 flex flex-col gap-1">
                {ALL_NAV.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-mobile-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `font-display text-3xl uppercase leading-tight px-2 py-1.5 ${
                        isActive ? "text-[hsl(var(--kerrys-coral))]" : "text-foreground hover:text-[hsl(var(--kerrys-sea))]"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
                <Button
                  asChild
                  className="mt-6 rounded-none bg-[hsl(var(--kerrys-coral))] text-foreground hover:bg-[hsl(18,95%,52%)] border-[0.5px] border-foreground font-bold uppercase tracking-[0.12em]"
                  data-testid="nav-mobile-book-button"
                >
                  <Link to="/book" onClick={() => setOpen(false)}>
                    Book a Class
                  </Link>
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export const Footer = () => (
  <footer className="bg-[hsl(var(--kerrys-ink))] text-background grain print:hidden" data-testid="main-footer">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-10">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-12 pb-16 border-b-[0.5px] border-background/25">
        <div className="md:col-span-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-background/50 mb-4">The short version</p>
          <p className="text-lg leading-relaxed text-background/85 max-w-sm">
            A dance school in Whitstable where every kid is welcome, the training is proper, and nobody sits at the back.
            Formerly Newcomb Theatre Arts.
          </p>
          <div className="flex gap-3 mt-8">
            <a
              href={SOCIALS.facebook}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Kerry's on Facebook"
              data-testid="footer-facebook-link"
              className="h-11 w-11 border-[0.5px] border-background/40 flex items-center justify-center hover:bg-[hsl(var(--kerrys-coral))] hover:text-foreground hover:border-transparent transition-colors"
            >
              <Facebook className="h-5 w-5" />
            </a>
            <a
              href={SOCIALS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Kerry's on Instagram"
              data-testid="footer-instagram-link"
              className="h-11 w-11 border-[0.5px] border-background/40 flex items-center justify-center hover:bg-[hsl(var(--kerrys-coral))] hover:text-foreground hover:border-transparent transition-colors"
            >
              <Instagram className="h-5 w-5" />
            </a>
          </div>
        </div>
        <div className="md:col-span-2">
          <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-background/50 mb-5">Go to</p>
          <ul className="space-y-2.5 text-sm">
            {ALL_NAV.map((item) => (
              <li key={item.to}>
                <Link to={item.to} className="text-background/80 hover:text-background font-medium transition-colors">
                  {item.label}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/book" className="text-[hsl(var(--kerrys-coral))] font-bold hover:text-background transition-colors">
                Book a Class
              </Link>
            </li>
          </ul>
        </div>
        <div className="md:col-span-3">
          <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-background/50 mb-5">Studios</p>
          <ul className="space-y-4 text-sm text-background/80">
            <li className="flex gap-2.5">
              <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--kerrys-sun))]" />
              <span>The Umbrella Centre, Oxford Street</span>
            </li>
            <li className="flex gap-2.5">
              <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--kerrys-sun))]" />
              <span>The Whitstable School &ndash; Dance Studio, Bellevue Road</span>
            </li>
            <li className="flex gap-2.5">
              <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--kerrys-sun))]" />
              <span>Whitstable Swimming Pool &ndash; Dance Studio, Tower Parade</span>
            </li>
            <li className="flex gap-2.5">
              <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--kerrys-sun))]" />
              <span>Treetops Dance Studio (private lessons)</span>
            </li>
          </ul>
        </div>
        <div className="md:col-span-3">
          <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-background/50 mb-5">Say hello</p>
          <ul className="space-y-3 text-sm text-background/80">
            <li>
              <a href={CONTACT.phoneHref} className="flex gap-2.5 hover:text-background transition-colors" data-testid="footer-phone-link">
                <Phone className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--kerrys-sun))]" />
                {CONTACT.phone}
              </a>
            </li>
            <li>
              <a href={CONTACT.emailHref} className="flex gap-2.5 hover:text-background transition-colors" data-testid="footer-email-link">
                <Mail className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--kerrys-sun))]" />
                {CONTACT.email}
              </a>
            </li>
            <li className="flex gap-2.5">
              <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--kerrys-sun))]" />
              {CONTACT.address}
            </li>
          </ul>
        </div>
      </div>

      <p className="font-display uppercase leading-[0.85] text-background/95 text-[19vw] lg:text-[13rem] select-none pt-10 -mb-4 lg:-mb-8" aria-hidden="true">
        Kerry<span className="text-[hsl(var(--kerrys-coral))]">&rsquo;s</span>
      </p>

      <div className="pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-background/50 border-t-[0.5px] border-background/25">
        <p>
          &copy; {new Date().getFullYear()} {SITE_NAME} (formerly Newcomb Theatre Arts) &middot; Whitstable, Kent
        </p>
        <p>
          <Link to="/admin/login" className="hover:text-background/80 transition-colors" data-testid="footer-admin-link">
            Staff login
          </Link>
        </p>
      </div>
    </div>
  </footer>
);

export const Layout = () => {
  const location = useLocation();
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(LOCAL_BUSINESS_JSONLD)}</script>
        <script type="application/ld+json">{JSON.stringify(WEBSITE_JSONLD)}</script>
      </Helmet>
      <Navbar key={location.pathname === "/" ? "home" : "page"} />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
};
